// /app/frontend/src/lib/cloverRecovery.js
//
// Milestone 4C (Feb 2026) — durable browser-restart recovery pointer
// for the Clover Station Duo kiosk checkout flow.
//
// Rationale
// ---------
// The kiosk (retail customer app) runs in a browser session on a
// physical device that customers can — and do — restart mid-payment.
// The previous Milestone 4B implementation used `sessionStorage`,
// which is wiped when the browser tab/process is closed, orphaning
// the active terminal payment. This module persists a minimal
// recovery HINT in `localStorage` so a full-restart still lands
// the customer back on the same Clover session.
//
// Storage contract (spec Part 8)
// ------------------------------
// * Key:   `rudras.clover.terminal.recovery.v1.<slug>`
// * Value: JSON { orderId, sessionId, slug, savedAt }
//          — `orderId`   : Rudras retail order id (string, required)
//          — `sessionId` : Clover terminal session id (string or null)
//          — `slug`      : tenant slug the pointer belongs to
//          — `savedAt`   : Date.now() when the pointer was written
// * NEVER store: card data, PAN, cardholder name, amount, tender
//   details, tokens, tenant secrets, or ANY payment authority.
//   The backend is the SOLE authority on payment state; this pointer
//   is purely a "where do I resume?" hint.
// * TTL: pointers older than STALE_MS (24h) are treated as stale and
//   ignored on read (and cleared, so we don't loop on them).
// * Tenant isolation: the key is namespaced by slug AND the pointer
//   payload also carries `slug`; a mismatch is treated as a miss.
//
// This module is deliberately framework-free (no React imports) so it
// can be unit-tested in plain Node against a mock storage. Any React
// hook wiring lives in the caller (`CheckoutModal.jsx`).

export const RECOVERY_KEY_PREFIX = "rudras.clover.terminal.recovery.v1.";
export const STALE_MS = 24 * 60 * 60 * 1000; // 24h

// Fields intentionally allow-listed. Anything else the caller might
// pass is dropped on write — a belt-and-braces guard against a future
// caller accidentally leaking amount/PAN/etc into localStorage.
const ALLOWED_FIELDS = ["orderId", "sessionId", "slug", "savedAt"];

export function buildRecoveryKey(slug) {
  if (!slug || typeof slug !== "string") return null;
  return `${RECOVERY_KEY_PREFIX}${slug}`;
}

/**
 * Persist a recovery pointer. Silently no-ops if `storage` is
 * unavailable, if `slug`/`orderId` are missing, or if the underlying
 * `setItem` throws (private-browsing quotas, disabled storage, etc).
 *
 * @param {Storage|null} storage e.g. window.localStorage
 * @param {string} slug tenant slug
 * @param {{ orderId: string, sessionId?: string|null, now?: number }} data
 * @returns {boolean} true if written, false otherwise
 */
export function saveRecovery(storage, slug, data) {
  const key = buildRecoveryKey(slug);
  if (!storage || !key) return false;
  const orderId = data && data.orderId;
  if (!orderId || typeof orderId !== "string") return false;
  const sessionId =
    data && typeof data.sessionId === "string" && data.sessionId
      ? data.sessionId
      : null;
  const savedAt = typeof data?.now === "number" ? data.now : Date.now();
  // Whitelist — never round-trip caller-supplied extras.
  const payload = { orderId, sessionId, slug, savedAt };
  try {
    storage.setItem(key, JSON.stringify(payload));
    return true;
  } catch (_e) {
    return false;
  }
}

/**
 * Read the recovery pointer for a given slug. Returns null when:
 *   * storage is unavailable
 *   * no pointer exists
 *   * the pointer's slug doesn't match (tenant isolation)
 *   * the pointer lacks an orderId
 *   * the pointer is older than `staleMs` (default STALE_MS)
 *
 * When a pointer is present but stale/invalid, this function ALSO
 * clears the storage entry to prevent the caller from re-observing
 * it on the next mount (which would produce an infinite recover
 * → dismiss → recover loop).
 *
 * @param {Storage|null} storage
 * @param {string} slug
 * @param {{ now?: number, staleMs?: number }} [opts]
 * @returns {{ orderId: string, sessionId: string|null, slug: string, savedAt: number }|null}
 */
export function readRecovery(storage, slug, opts) {
  const key = buildRecoveryKey(slug);
  if (!storage || !key) return null;
  let raw;
  try {
    raw = storage.getItem(key);
  } catch (_e) {
    return null;
  }
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_e) {
    // Corrupt entry — clear it and pretend it was never there.
    safeRemove(storage, key);
    return null;
  }
  if (!parsed || typeof parsed !== "object") {
    safeRemove(storage, key);
    return null;
  }
  if (parsed.slug !== slug || !parsed.orderId) {
    safeRemove(storage, key);
    return null;
  }
  const now = typeof opts?.now === "number" ? opts.now : Date.now();
  const staleMs = typeof opts?.staleMs === "number" ? opts.staleMs : STALE_MS;
  const savedAt = Number(parsed.savedAt);
  if (!Number.isFinite(savedAt) || now - savedAt > staleMs) {
    safeRemove(storage, key);
    return null;
  }
  // Only surface allow-listed fields — belt-and-braces so a future
  // rogue writer can't smuggle sensitive data through the reader.
  const out = {};
  for (const f of ALLOWED_FIELDS) out[f] = parsed[f] ?? null;
  return out;
}

/**
 * Remove the pointer for `slug`. Safe to call when no pointer exists.
 */
export function clearRecovery(storage, slug) {
  const key = buildRecoveryKey(slug);
  if (!storage || !key) return;
  safeRemove(storage, key);
}

function safeRemove(storage, key) {
  try {
    storage.removeItem(key);
  } catch (_e) {
    // best-effort — nothing else we can do
  }
}
